import React, { useState, useMemo, useEffect } from "react";
import { Card, Avatar, Tag, Spin, DatePicker } from "antd";
import moment from "moment";
import "./TeamAttendanceWidget.css";

const STATUS_CONFIG = {
  Present: { color: "green", className: "team-attendance-widget__tag--present" },
  Late: { color: "orange", className: "team-attendance-widget__tag--late" },
  Leave: { color: "blue", className: "team-attendance-widget__tag--leave" },
  Absent: { color: "red", className: "team-attendance-widget__tag--absent" },
};

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatCheckIn(time) {
  if (time == null || time === "" || time === "--") return "Check-in: --";
  const t = String(time).trim();
  if (/^\d{1,2}:\d{2}$/.test(t) || /^\d{1,2}:\d{2}:\d{2}$/.test(t)) return `Check-in: ${t}`;
  return `Check-in: ${t}`;
}

/** Map daily_status code to display label */
function getStatusLabel(dailyStatus) {
  const s = String(dailyStatus || "").trim().toUpperCase();
  if (s === "A") return "Absent";
  if (s === "L" || s === "L-EO") return "Late";
  if (s === "LV" || s === "HLV") return "Leave";
  return "Present";
}

/** UTC date string (e.g. 2026-01-31T18:00:00.000Z) + offset hours → local date YYYY-MM-DD */
const UTC_OFFSET_HOURS = 6;

function getLocalDateStr(utcDateStr) {
  if (!utcDateStr) return "";
  const m = moment.utc(utcDateStr).add(UTC_OFFSET_HOURS, "hours");
  return m.isValid() ? m.format("YYYY-MM-DD") : String(utcDateStr).slice(0, 10);
}

function isFutureDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return false;
  const today = moment().format("YYYY-MM-DD");
  const localDate = getLocalDateStr(dateStr) || String(dateStr).slice(0, 10);
  return localDate > today;
}

/** Normalize prop: accept array, { list }, or [{ data: [...] }] from API */
function getRawList(teamAttendanceData) {
  if (!teamAttendanceData) return [];
  if (Array.isArray(teamAttendanceData)) {
    const first = teamAttendanceData[0];
    if (first && Array.isArray(first.data)) return first.data;
    return teamAttendanceData;
  }
  if (Array.isArray(teamAttendanceData.list)) return teamAttendanceData.list;
  return [];
}

export function TeamAttendanceWidget({ teamAttendanceData, loading: propLoading }) {
  const rawList = useMemo(() => getRawList(teamAttendanceData), [teamAttendanceData]);

  const [selectedDate, setSelectedDate] = useState(moment());

  // When data arrives, set selected date to the local date (UTC + 6h) so the list is not empty
  useEffect(() => {
    if (rawList.length > 0) {
      const d = rawList[0].day_is || rawList[0].attendance_date || rawList[0].date;
      if (d) {
        const localDateStr = getLocalDateStr(d);
        if (localDateStr) {
          const parsed = moment(localDateStr, "YYYY-MM-DD");
          if (parsed.isValid()) setSelectedDate(parsed);
        }
      }
    }
  }, [teamAttendanceData]);

  const dateStr = selectedDate.format("YYYY-MM-DD");

  const list = useMemo(() => {
    if (!rawList.length) return [];
    return rawList.filter((item) => {
      const d = item.day_is || item.attendance_date || item.date;
      if (d == null) return true;
      const itemDate = getLocalDateStr(d) || String(d).slice(0, 10);
      return itemDate === dateStr;
    });
  }, [rawList, dateStr]);

  const summary = useMemo(() => {
    const counts = { present: 0, late: 0, leave: 0, absent: 0 };
    const statusPresent = ["P", "WP", "HP", "L", "EO", "L-EO", "WFH", "HLV"];
    list.forEach((item) => {
      const status = (item.daily_status || item.status || "").trim().toUpperCase();

      if (statusPresent.includes(status)) {
        counts.present++;
      } else if (status === "A") {
        const dayStr = item.day_is || item.attendance_date || item.date;
        if (!isFutureDate(dayStr)) {
          counts.absent++;
        }
      }
      if (status === "LV" || status === "HLV") {
        counts.leave++;
      }
      if (status === "L" || status === "L-EO") {
        counts.late++;
      }
    });
    return counts;
  }, [list]);

  const isLoading = Boolean(propLoading);

  if (isLoading && list.length === 0) {
    return (
      <div className="team-attendance-widget">
        <Card className="team-attendance-widget__card" bordered={false}>
          <div className="team-attendance-widget__loading">
            <Spin tip="Loading team attendance..." />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="team-attendance-widget">
      <div className="team-attendance-widget__card" bordered={false}>
        <div className="team-attendance-widget__header">
          <h2 className="team-attendance-widget__title"></h2>
          <DatePicker
            value={selectedDate}
            onChange={(date) => setSelectedDate(date || moment())}
            format="DD MMM YYYY"
            disabled={true}
            className="team-attendance-widget__date-picker"
            allowClear={false}
          />
        </div>
        <div className="team-attendance-widget__body">
          <div className="team-attendance-widget__list">
            {list.length === 0 ? (
              <p className="team-attendance-widget__empty">
                No attendance records for this date.
              </p>
            ) : (
              list.map((item) => {
                const name = item.name || item.employee_name || item.employee || "—";
                const statusLabel = getStatusLabel(item.daily_status || item.status);
                const config = STATUS_CONFIG[statusLabel] || STATUS_CONFIG.Present;
                const checkIn = item.check_in_time ?? item.check_in ?? item.in_time ?? null;
                return (
                  <div
                    key={item.id ?? item.employee_id ?? item.user_id ?? name}
                    className="team-attendance-widget__item"
                  >
                    <div className="team-attendance-widget__item-left">
                      <Avatar
                        size={40}
                        src={item.imageUrl || item.image_url || null}
                        className="team-attendance-widget__avatar"
                      >
                        {!item.imageUrl && !item.image_url ? getInitials(name) : null}
                      </Avatar>
                      <div className="team-attendance-widget__item-details">
                        <p className="team-attendance-widget__item-name">{name}</p>
                        <p className="team-attendance-widget__item-checkin">
                          {formatCheckIn(checkIn)}
                        </p>
                      </div>
                    </div>
                    <Tag color={config.color} className={`team-attendance-widget__tag ${config.className}`}>
                      {statusLabel}
                    </Tag>
                  </div>
                );
              })
            )}
          </div>
          <div className="team-attendance-widget__summary">
            <div className="team-attendance-widget__summary-box team-attendance-widget__summary-box--present">
              <span className="team-attendance-widget__summary-count">{summary.present}</span>
              <span className="team-attendance-widget__summary-label">Present</span>
            </div>
            <div className="team-attendance-widget__summary-box team-attendance-widget__summary-box--late">
              <span className="team-attendance-widget__summary-count">{summary.late}</span>
              <span className="team-attendance-widget__summary-label">Late</span>
            </div>
            <div className="team-attendance-widget__summary-box team-attendance-widget__summary-box--leave">
              <span className="team-attendance-widget__summary-count">{summary.leave}</span>
              <span className="team-attendance-widget__summary-label">Leave</span>
            </div>
            <div className="team-attendance-widget__summary-box team-attendance-widget__summary-box--absent">
              <span className="team-attendance-widget__summary-count">{summary.absent}</span>
              <span className="team-attendance-widget__summary-label">Absent</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeamAttendanceWidget;
